"use client";
import { CheckIcon } from "@cmp-src/icons/icons";
import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import { Button } from "@cmp-src/primitives/button";
import { Checkbox } from "@cmp-src/primitives/checkbox";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  AiChatToolShowReleaseReservationsOutput,
  AiChatToolShowReleaseReservationsPart,
  ISchedule
} from "@gettactic/api";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { useAuthenticated, useTacticHooks } from "@gettactic/hooks";
import { format, parseISO } from "date-fns";
import React from "react";
import { toast } from "react-hot-toast";
import { invalidateReservationQueries } from "@/features/reserve/components/Reserve/helpers";
import {
  RELEASE_TO_ANYONE_SELECTION,
  useReleaseDedicatedDesk
} from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import {
  nextReleaseProgress,
  type ReleaseProgress
} from "@/features/tessa/components/tools/reservation-actions/reservationActions";
import { useSingleFlight } from "@/features/tessa/components/utils/useSingleFlight";
import { useTessaMessageState } from "@/features/tessa/components/utils/useTessaMessageState";
import { TessaMessageRendererProps } from "@/features/tessa/types";
import { api } from "@/lib/api/api";

// Schedule times are wall clock times with a trailing Z, not UTC
const scheduleTime = (schedule: ISchedule) =>
  `${format(new Date(dateStringToLocalTime(schedule.start)), "p")} – ${format(new Date(dateStringToLocalTime(schedule.end)), "p")}`;

const dayLabel = (date: string | undefined) =>
  date ? format(parseISO(date), "EEE, MMM d") : "that day";

interface ReleasedState {
  released: string[];
  dedicatedDeskName: string | null;
}

/**
 * The user's reservations for a day, with their dedicated desk, to release
 * when they aren't coming in.
 */
export const ChatBotToolShowReleaseReservations: React.FC<
  TessaMessageRendererProps<AiChatToolShowReleaseReservationsPart>
> = ({ part, message }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowReleaseReservationsOutput
    | undefined;
  const askTessa = useAskTessa();
  const messageState = useTessaMessageState(
    askTessa?.selectedChatId,
    message,
    part
  );
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { queryClient } = useTacticHooks();
  const { release: releaseDedicatedDesk } = useReleaseDedicatedDesk();

  const schedules = (output?.schedule_ids ?? [])
    .map((id) => output?._metadata?.schedules_by_id?.[id])
    .filter((schedule): schedule is ISchedule => !!schedule);
  const dedicatedDesk = output?._metadata?.dedicated_resources?.[0];

  // Every reservation starts selected, also the ones that stream in after the
  // first render, so only the ones the user unchecks are kept
  const [unchecked, setUnchecked] = React.useState<Record<string, boolean>>({});
  const [releaseDesk, setReleaseDesk] = React.useState(true);
  const { run: runOnce, isRunning: isReleasing } = useSingleFlight();
  // What earlier attempts released, saved with the message so a reload doesn't offer it again; only a fully
  // successful attempt completes the card
  const [progress, setProgress] = React.useState<ReleaseProgress>(
    () =>
      (messageState?.state === "partial"
        ? (messageState.data?.progress as ReleaseProgress | undefined)
        : undefined) ?? { released: [], deskReleased: false }
  );

  if (!output) return <TacticLoader className={"opacity-60"} />;

  const done =
    messageState?.state === "completed"
      ? (messageState.data?.data as ReleasedState | undefined)
      : undefined;
  const toRelease = schedules.filter(
    (schedule) =>
      !unchecked[schedule.id] && !progress.released.includes(schedule.id)
  );
  const willReleaseDesk =
    !!dedicatedDesk && !!output.date && releaseDesk && !progress.deskReleased;

  const release = () => runOnce(releaseSelected);

  const releaseSelected = async () => {
    const results = await Promise.allSettled(
      toRelease.map((schedule) =>
        api.client.schedules.delete({
          schedule: schedule.id,
          recurring: false,
          slug: authenticatedUser.slug ?? "",
          resourceId: schedule.resource?.id ?? ""
        })
      )
    );
    const released = toRelease
      .filter((_, index) => {
        const result = results[index];
        return result?.status === "fulfilled" && result.value.ok;
      })
      .map((schedule) => schedule.id);
    if (released.length < toRelease.length) {
      toast.error(
        `${toRelease.length - released.length} of ${toRelease.length} reservations couldn't be released.`
      );
    }

    let deskSucceeded = false;
    if (willReleaseDesk && output.date) {
      deskSucceeded = await releaseDedicatedDesk({
        deskId: dedicatedDesk.id,
        deskName: dedicatedDesk.name,
        startsOn: output.date,
        endsOn: output.date,
        officeId: dedicatedDesk.office_id,
        selection: RELEASE_TO_ANYONE_SELECTION,
        reason: ""
      });
    }

    await invalidateReservationQueries(queryClient);
    const next = nextReleaseProgress(progress, {
      attempted: toRelease.map((schedule) => schedule.id),
      succeeded: released,
      deskAttempted: willReleaseDesk,
      deskSucceeded
    });
    setProgress({ released: next.released, deskReleased: next.deskReleased });
    if (!next.complete && (next.released.length > 0 || next.deskReleased)) {
      await messageState?.setMessageState("partial", {
        data: null,
        progress: {
          released: next.released,
          deskReleased: next.deskReleased
        } satisfies ReleaseProgress
      });
    }
    if (next.complete) {
      await messageState?.setMessageState("completed", {
        data: {
          released: next.released,
          dedicatedDeskName: next.deskReleased
            ? (dedicatedDesk?.name ?? null)
            : null
        } satisfies ReleasedState
      });
    }
  };

  if (done) {
    return (
      <ChatBotToolCard toolId={part.toolCallId}>
        <div className={"flex items-start gap-2"}>
          <CheckIcon className={"size-6 shrink-0 text-green-500"} />
          <div>
            <p>
              Released {done.released.length}{" "}
              {done.released.length === 1 ? "reservation" : "reservations"} for{" "}
              {dayLabel(output.date)}.
            </p>
            {done.dedicatedDeskName && (
              <p className={"text-neutral-600"}>
                {done.dedicatedDeskName} is free for anyone that day.
              </p>
            )}
          </div>
        </div>
      </ChatBotToolCard>
    );
  }

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      <div className={"flex min-w-[320px] flex-col gap-3"}>
        <p className={"font-medium"}>Release {dayLabel(output.date)}</p>
        {schedules.length === 0 && (
          <p className={"text-neutral-600"}>
            You have no reservations that day.
          </p>
        )}
        <ul className={"flex flex-col gap-2"}>
          {schedules.map((schedule) => (
            <li key={schedule.id}>
              <label
                htmlFor={`release-${schedule.id}`}
                className={
                  "flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3"
                }
              >
                <Checkbox
                  id={`release-${schedule.id}`}
                  disabled={progress.released.includes(schedule.id)}
                  checked={
                    progress.released.includes(schedule.id) ||
                    !unchecked[schedule.id]
                  }
                  onCheckedChange={(checked) =>
                    setUnchecked((current) => ({
                      ...current,
                      [schedule.id]: checked !== true
                    }))
                  }
                />
                <span className={"flex flex-col"}>
                  <span className={"font-medium"}>
                    {schedule.title || schedule.resource?.name}
                  </span>
                  <span className={"text-neutral-600 text-sm"}>
                    {schedule.title ? `${schedule.resource?.name} · ` : ""}
                    {scheduleTime(schedule)}
                    {progress.released.includes(schedule.id) && " · Released"}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        {dedicatedDesk && output.date && (
          <label
            htmlFor={"release-dedicated-desk"}
            className={
              "flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3"
            }
          >
            <Checkbox
              id={"release-dedicated-desk"}
              disabled={progress.deskReleased}
              checked={progress.deskReleased || releaseDesk}
              onCheckedChange={(checked) => setReleaseDesk(checked === true)}
            />
            <span className={"flex flex-col"}>
              <span className={"font-medium"}>{dedicatedDesk.name}</span>
              <span className={"text-neutral-600 text-sm"}>
                {progress.deskReleased
                  ? "Released, free for anyone that day"
                  : "Your dedicated desk, free for anyone that day"}
              </span>
            </span>
          </label>
        )}
        <Button
          variant={"destructive"}
          isLoading={isReleasing}
          disabled={toRelease.length === 0 && !willReleaseDesk}
          onClick={release}
        >
          Release
        </Button>
      </div>
    </ChatBotToolCard>
  );
};
